import React, { useMemo } from 'react';
import { Box, Typography, Chip, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';

import AccordionWrapper from 'components/ChecklistReport/AccordionWrapper';
import { scoreSummaryStyles, scoreClassNames } from './scoreSummary/styles';

interface ScoreData {
  percentage: number;
  earned: number;
  total: number;
}

interface ScoreItem {
  id: string;
  description?: string;
  title?: string;
  score?: ScoreData;
  subSections?: ScoreItem[];
}

interface ScoreSummaryProps {
  items: ScoreItem[];
  reference: React.RefObject<HTMLDivElement>;
  log: {
    score?: ScoreData;
  };
}

// Helper function to check if an item has a valid score
const hasValidScore = (item: ScoreItem): boolean => {
  return Boolean(item.score?.total && item.score.total > 0);
};

// Helper function to get display text for an item
const getItemDisplayText = (item: ScoreItem): string => {
  return item.description || item.title || 'Untitled';
};

// Optimized recursive function to get items with scores
const getItemsWithScores = (items: ScoreItem[]): ScoreItem[] => {
  const result: ScoreItem[] = [];

  for (const item of items) {
    if (hasValidScore(item)) {
      result.push(item);
    } else if (item.subSections?.length) {
      const scoredSubItems = getItemsWithScores(item.subSections);
      if (scoredSubItems.length > 0) {
        result.push({
          ...item,
          subSections: scoredSubItems,
        });
      }
    }
  }

  return result;
};

// Optimized recursive function to calculate total scores
const calculateTotalScore = (items: ScoreItem[]): ScoreData => {
  let totalEarned = 0;
  let totalPoints = 0;

  const processItems = (itemList: ScoreItem[]) => {
    for (const item of itemList) {
      if (hasValidScore(item)) {
        totalEarned += item?.score?.earned || 0;
        totalPoints += item?.score?.total || 0;
      }
    }
  };

  processItems(items);

  return {
    earned: totalEarned,
    total: totalPoints,
    percentage:
      totalPoints > 0 ? Math.round((totalEarned / totalPoints) * 100) : 0,
  };
};

// Component to render individual score items
const ScoreItemRenderer: React.FC<{
  item: ScoreItem;
  level: number;
  index: number;
  totalItems: number;
}> = ({ item, level, index, totalItems }) => {
  const theme = useTheme();
  const hasScore = hasValidScore(item);
  const scoredSubSections = useMemo(() => {
    return item.subSections?.filter(hasValidScore) || [];
  }, [item.subSections]);

  return (
    <Box key={item.id}>
      {hasScore && (
        <Box
          className={scoreClassNames.scoreRow}
          sx={scoreSummaryStyles.scoreRow(theme, level)}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Typography
              className={scoreClassNames.scoreLabel}
              sx={scoreSummaryStyles.scoreLabel(level)}
            >
              {getItemDisplayText(item)}
            </Typography>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Chip
                label={`${item?.score?.percentage}%`}
                size="small"
                className={scoreClassNames.percentageChip}
                sx={scoreSummaryStyles.percentageChip(
                  theme,
                  level,
                  item?.score?.percentage || 0,
                )}
              />
              <Typography
                className={scoreClassNames.pointsText}
                sx={scoreSummaryStyles.pointsText(level)}
              >
                ({item?.score?.earned} out of {item?.score?.total} pts)
              </Typography>
            </Stack>
          </Stack>
        </Box>
      )}

      {scoredSubSections.length > 0 && (
        <Box sx={{ marginLeft: level === 0 ? 0 : 0.5 }}>
          {scoredSubSections.map((subItem, subIndex) => (
            <ScoreItemRenderer
              key={subItem.id}
              item={subItem}
              level={level + 1}
              index={subIndex}
              totalItems={scoredSubSections.length}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const ScoreSummary: React.FC<ScoreSummaryProps> = ({
  items,
  reference,
  log,
}) => {
  const theme = useTheme();
  const scoredItems = useMemo(() => getItemsWithScores(items || []), [items]);

  const remainingScoreItem = useMemo((): ScoreItem | null => {
    if (!log?.score?.total) return null;

    const totalScoredItems = calculateTotalScore(scoredItems);
    const remainingEarned = log.score.earned - totalScoredItems.earned;
    const remainingTotal = log.score.total - totalScoredItems.total;

    if (remainingTotal <= 0) return null;

    const remainingPercentage = Math.round(
      (remainingEarned / remainingTotal) * 100,
    );

    return {
      id: 'untitled-sections',
      title: 'Untitled Sections',
      score: {
        percentage: remainingPercentage,
        earned: remainingEarned,
        total: remainingTotal,
      },
    };
  }, [log?.score, scoredItems]);

  const itemsToDisplay = useMemo(() => {
    return remainingScoreItem
      ? [remainingScoreItem, ...scoredItems]
      : scoredItems;
  }, [remainingScoreItem, scoredItems]);

  const hasOverallScore = Boolean(log?.score?.total && log.score.total > 0);

  return (
    <Box className="mb-3" id="scoreSummary" ref={reference}>
      <AccordionWrapper
        headerName={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" fontWeight={600} color="#1A1A1A">
              Score Summary
            </Typography>
          </Stack>
        }
        headerEndRenderer={
          hasOverallScore ? (
            <Chip
              label={`Overall Score: ${log?.score?.percentage}% (${log?.score?.earned} of ${log?.score?.total} pts)`}
              size="small"
              className={scoreClassNames.overallScoreChip}
              sx={scoreSummaryStyles.overallScoreChip}
            />
          ) : null
        }
      >
        <Box
          className={scoreClassNames.scoreContainer}
          sx={scoreSummaryStyles.scoreContainer}
        >
          <Box
            className={scoreClassNames.contentContainer}
            sx={scoreSummaryStyles.contentContainer}
          >
            {itemsToDisplay.length > 0 ? (
              <Stack spacing={0}>
                {itemsToDisplay.map((item, index) => (
                  <ScoreItemRenderer
                    key={item.id}
                    item={item}
                    level={0}
                    index={index}
                    totalItems={itemsToDisplay.length}
                  />
                ))}
              </Stack>
            ) : (
              <Box
                className={scoreClassNames.emptyState}
                sx={scoreSummaryStyles.emptyState(theme)}
              >
                <Typography variant="body2">
                  No scored items available
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </AccordionWrapper>
    </Box>
  );
};

export default ScoreSummary;
